import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { useAuth } from '../../components/router/authContext';
import { hasProductAccess } from '../../utils/productAccess';
import { FaFileExcel, FaChevronDown, FaChevronRight, FaTrash, FaColumns } from 'react-icons/fa';
import CircuitUpdateBanner from '../../components/common/CircuitUpdateBanner';
import AdditionalMatrices from '../../components/regulator/AdditionalMatrices';
import { getAssessmentMetaByLabel } from '../../utils/capacityAssessment';

const PAGE_SIZE = 25;
const SELECT_COLUMN_WIDTH = 48;
const SITE_NAME_COLUMN_WIDTH = 220;
const ADDRESS_COLUMN_WIDTH = 320;
const SITE_NAME_COLUMN_LEFT = SELECT_COLUMN_WIDTH;
const ADDRESS_COLUMN_LEFT = SELECT_COLUMN_WIDTH + SITE_NAME_COLUMN_WIDTH;
const DEFAULT_SELECTED_METRIC_KEYS = [
  'utility_name', 'expected_capacity_mw', 'expected_year', 'capacity_assessment_status',
  'county', 'is_scaqmd', 'sra_monitoring', 'is_dac', 'nearby_circuit_capacity_mw',
  'nearest_a_bank_name', 'nearest_a_bank_capacity_mw',
  'warehouse_count_5mi', 'warehouse_acres_5mi', 'intermodal_count_5mi',
];
const DEFAULT_A_BANK_METRIC_KEYS = ['nearest_a_bank_name', 'nearest_a_bank_capacity_mw'];
const BASE_KEYS = ['address', 'raw_address', 'latitude', 'longitude', 'processing_status', 'error_message'];
const LEGACY_METRIC_KEYS = new Set([
  'nearby_circuit_capacity_mw',
  'nearby_circuit_capacity_mw_range',
  'circuit_name',
]);

const formatAcres = (acres, decimals = 1) => {
  if (acres == null || acres === '') return '—';
  const n = Number(acres);
  if (Number.isNaN(n)) return '—';
  return n.toFixed(decimals);
};

const formatMetricValue = (key, value) => {
  if (value == null || value === '') return '—';
  if (key && key.includes('stall_count') && Number(value) === 0) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') return value;
  if (key && key.startsWith('warehouse_acres')) return formatAcres(value);
  if (key && (key.startsWith('avg_stops_per_day_') || key.startsWith('avg_stop_duration_hours_'))) {
    const n = Number(value);
    return Number.isNaN(n) ? '—' : n.toFixed(1);
  }
  if (
    key === 'nearby_circuit_capacity_mw' ||
    key === 'nearest_circuit_capacity_mw' ||
    key === 'max_nearby_circuit_capacity_mw' ||
    key === 'nearest_substation_capacity_mw' ||
    key === 'nearest_a_bank_capacity_mw' ||
    (key && key.includes('power_kw'))
  ) {
    const n = Number(value);
    return Number.isNaN(n) ? '—' : n.toFixed(2);
  }
  if (
    key === 'nearest_circuit_distance_ft' ||
    key === 'max_nearby_circuit_distance_ft' ||
    key === 'nearest_substation_distance_ft'
  ) {
    const n = Number(value);
    return Number.isNaN(n) ? '—' : Math.round(n).toLocaleString();
  }
  if (Number.isInteger(value) || (typeof value === 'number' && Number.isInteger(value))) return String(value);
  const n = Number(value);
  return Number.isNaN(n) ? '—' : n.toFixed(2);
};

const REGULATOR_SELECTED_METRICS_KEY = 'regulator_table_selected_metrics';
const REGULATOR_ADDITIONAL_MATRICES_KEY = 'regulator_table_additional_matrices';

const loadSelectedMetricKeys = () => {
  try {
    const stored = localStorage.getItem(REGULATOR_SELECTED_METRICS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        const next = [...parsed];
        DEFAULT_A_BANK_METRIC_KEYS.forEach((key) => {
          if (!next.includes(key)) next.push(key);
        });
        return next;
      }
    }
  } catch (_) {}
  return DEFAULT_SELECTED_METRIC_KEYS;
};

const loadShowAdditionalMatrices = () => {
  try {
    const stored = localStorage.getItem(REGULATOR_ADDITIONAL_MATRICES_KEY);
    if (stored === 'false') return false;
    if (stored === 'true') return true;
  } catch (_) {}
  return true;
};

const RegulatorTableView = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [locations, setLocations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [metricsList, setMetricsList] = useState([]);
  const [metricsSelectorOpen, setMetricsSelectorOpen] = useState(false);
  const metricsSelectorRef = useRef(null);
  const metricsDropdownRef = useRef(null);
  const [metricsDropdownPosition, setMetricsDropdownPosition] = useState({ left: 0, top: 0 });
  const [metricsDropdownPositionReady, setMetricsDropdownPositionReady] = useState(false);
  const categorySelectAllRefs = useRef({});
  const [selectedMetricKeys, setSelectedMetricKeys] = useState(loadSelectedMetricKeys);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  const [deleteLocationsLoading, setDeleteLocationsLoading] = useState(false);
  const [updateMetricsLoading, setUpdateMetricsLoading] = useState(false);
  const [updateCompleteCount, setUpdateCompleteCount] = useState(0);
  const [updateCompleteOpen, setUpdateCompleteOpen] = useState(false);
  const [editingSiteNameId, setEditingSiteNameId] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [showAdditionalMatrices, setShowAdditionalMatrices] = useState(loadShowAdditionalMatrices);
  const [editingSiteNameValue, setEditingSiteNameValue] = useState('');
  const [savingSiteNameId, setSavingSiteNameId] = useState(null);
  const [runStatus, setRunStatus] = useState(null);
  const [circuitStaleness, setCircuitStaleness] = useState({ count: 0, locationIds: [] });
  const [cancelRunLoading, setCancelRunLoading] = useState(false);
  const statusPollingRef = useRef(null);
  const pendingManualUpdateCountRef = useRef(null);
  const authToken = useMemo(() => localStorage.getItem('authToken'), []);
  const tableScrollRef = useRef(null);
  const horizontalScrollRef = useRef(null);
  const tableRef = useRef(null);
  const scrollSyncInProgressRef = useRef(false);
  const [horizontalScrollVisible, setHorizontalScrollVisible] = useState(false);
  const [horizontalScrollWidth, setHorizontalScrollWidth] = useState(0);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const syncHorizontalMetrics = useCallback(() => {
    const scroller = tableScrollRef.current;
    if (!scroller) return;
    setHorizontalScrollWidth(scroller.scrollWidth || 0);
    setHorizontalScrollVisible((scroller.scrollWidth || 0) > (scroller.clientWidth || 0) + 1);
    if (horizontalScrollRef.current && !scrollSyncInProgressRef.current) {
      horizontalScrollRef.current.scrollLeft = scroller.scrollLeft;
    }
  }, []);

  const handleTableScroll = useCallback(() => {
    const scroller = tableScrollRef.current;
    const bar = horizontalScrollRef.current;
    if (!scroller || !bar) return;
    if (scrollSyncInProgressRef.current) return;
    scrollSyncInProgressRef.current = true;
    bar.scrollLeft = scroller.scrollLeft;
    requestAnimationFrame(() => {
      scrollSyncInProgressRef.current = false;
    });
  }, []);

  const handleHorizontalScroll = useCallback(() => {
    const scroller = tableScrollRef.current;
    const bar = horizontalScrollRef.current;
    if (!scroller || !bar) return;
    if (scrollSyncInProgressRef.current) return;
    scrollSyncInProgressRef.current = true;
    scroller.scrollLeft = bar.scrollLeft;
    requestAnimationFrame(() => {
      scrollSyncInProgressRef.current = false;
    });
  }, []);

  useEffect(() => {
    syncHorizontalMetrics();
  }, [syncHorizontalMetrics, selectedMetricKeys, locations.length, loading]);

  useEffect(() => {
    syncHorizontalMetrics();
    const scroller = tableScrollRef.current;
    const tableEl = tableRef.current;
    if (!scroller) return;

    const onResize = () => syncHorizontalMetrics();
    window.addEventListener('resize', onResize);

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => syncHorizontalMetrics());
      ro.observe(scroller);
      if (tableEl) ro.observe(tableEl);
    }

    return () => {
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
    };
  }, [syncHorizontalMetrics]);

  const stopStatusPolling = useCallback(() => {
    if (statusPollingRef.current) {
      clearInterval(statusPollingRef.current);
      statusPollingRef.current = null;
    }
  }, []);

  const shouldContinuePolling = useCallback((statusData) => {
    if (!statusData || typeof statusData !== 'object') return false;
    const pendingCount = Number(statusData.pending_count || 0);
    const runningCount = Number(statusData.running_count || 0);
    if (pendingCount + runningCount > 0) return true;
    return statusData.status === 'running';
  }, []);

  const selectableMetrics = useMemo(
    () => metricsList.filter((m) => !BASE_KEYS.includes(m.key)),
    [metricsList]
  );
  const metricsByCategory = useMemo(() => {
    const map = {};
    selectableMetrics.forEach((m) => {
      const cat = m.category || 'Other';
      if (!map[cat]) map[cat] = [];
      map[cat].push(m);
    });
    return map;
  }, [selectableMetrics]);
  const keyToLabel = useMemo(() => {
    const map = {};
    metricsList.forEach((m) => { map[m.key] = m.label; });
    return map;
  }, [metricsList]);
  const selectedMetricsOrdered = useMemo(() => {
    const order = metricsList.map((m) => m.key);
    return selectedMetricKeys.filter((k) => order.includes(k)).sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }, [selectedMetricKeys, metricsList]);

  const fetchLocations = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(PAGE_SIZE));
      if (search.trim()) params.set('search', search.trim());
      const { data } = await axios.get(
        `${API_ENDPOINTS.regulatorLocations}?${params}`,
        { headers: { Authorization: `Token ${authToken}` } }
      );
      setLocations(data.results || []);
      setTotalCount(data.count ?? 0);
    } catch {
      setLocations([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [authToken, page, search]);

  useEffect(() => {
    fetchLocations();
  }, [page, search, fetchLocations]);

  const pollStatus = useCallback(async () => {
    if (!authToken) return;
    try {
      const { data } = await axios.get(API_ENDPOINTS.regulatorStatus, {
        headers: { Authorization: `Token ${authToken}` },
      });
      setRunStatus(data);
      if (shouldContinuePolling(data)) return;
      if (pendingManualUpdateCountRef.current != null) {
        if (data?.status === 'completed') {
          setUpdateCompleteCount(pendingManualUpdateCountRef.current);
          setUpdateCompleteOpen(true);
        }
        pendingManualUpdateCountRef.current = null;
      }
      stopStatusPolling();
      fetchLocations();
    } catch {
      setRunStatus(null);
      stopStatusPolling();
    }
  }, [authToken, fetchLocations, shouldContinuePolling, stopStatusPolling]);

  const startStatusPolling = useCallback(() => {
    stopStatusPolling();
    statusPollingRef.current = setInterval(pollStatus, 2000);
  }, [pollStatus, stopStatusPolling]);

  const handleCancelRun = useCallback(async () => {
    if (!authToken || cancelRunLoading) return;
    setCancelRunLoading(true);
    try {
      await axios.post(
        API_ENDPOINTS.regulatorCancel,
        {},
        { headers: { Authorization: `Token ${authToken}` } }
      );
      await pollStatus();
    } catch {
      // Best-effort; status polling will reconcile.
    } finally {
      setCancelRunLoading(false);
    }
  }, [authToken, cancelRunLoading, pollStatus]);

  useEffect(() => {
    if (!authToken) return;
    const checkOnce = async () => {
      try {
        const { data } = await axios.get(API_ENDPOINTS.regulatorStatus, {
          headers: { Authorization: `Token ${authToken}` },
        });
        setRunStatus(data);
        if (shouldContinuePolling(data)) {
          startStatusPolling();
        }
      } catch {
        setRunStatus(null);
      }
    };
    checkOnce();
    return () => {
      stopStatusPolling();
    };
  }, [authToken, shouldContinuePolling, startStatusPolling, stopStatusPolling]);

  const fetchMetrics = useCallback(async () => {
    if (!authToken) return;
    try {
      const { data } = await axios.get(API_ENDPOINTS.regulatorMetrics, {
        headers: { Authorization: `Token ${authToken}` },
      });
      setMetricsList(Array.isArray(data) ? data : []);
    } catch {
      setMetricsList([]);
    }
  }, [authToken]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    try {
      localStorage.setItem(REGULATOR_SELECTED_METRICS_KEY, JSON.stringify(selectedMetricKeys));
    } catch (_) {}
  }, [selectedMetricKeys]);

  useEffect(() => {
    try {
      localStorage.setItem(REGULATOR_ADDITIONAL_MATRICES_KEY, showAdditionalMatrices ? 'true' : 'false');
    } catch (_) {}
  }, [showAdditionalMatrices]);

  useEffect(() => {
    Object.entries(metricsByCategory).forEach(([category, items]) => {
      const categoryKeys = items.map((m) => m.key);
      const allSelected = categoryKeys.length > 0 && categoryKeys.every((k) => selectedMetricKeys.includes(k));
      const someSelected = categoryKeys.some((k) => selectedMetricKeys.includes(k));
      const el = categorySelectAllRefs.current[category];
      if (el) el.indeterminate = someSelected && !allSelected;
    });
  }, [selectedMetricKeys, metricsByCategory]);

  const toggleMetricSelection = useCallback((key) => {
    setSelectedMetricKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, []);

  const toggleCategorySelection = useCallback((categoryKeys) => {
    const keysSet = new Set(categoryKeys);
    const allSelected = categoryKeys.every((k) => selectedMetricKeys.includes(k));
    setSelectedMetricKeys((prev) => {
      if (allSelected) {
        return prev.filter((k) => !keysSet.has(k));
      }
      const next = new Set(prev);
      categoryKeys.forEach((k) => next.add(k));
      return [...next];
    });
  }, [selectedMetricKeys]);

  const handleExportXlsx = useCallback(() => {
    if (!authToken) return;
    const params = new URLSearchParams();
    params.set('metrics', selectedMetricsOrdered.join(','));
    const url = `${API_ENDPOINTS.regulatorExportXlsx}?${params}`;
    const link = document.createElement('a');
    link.setAttribute('download', `regulator_locations.xlsx`);
    document.head.appendChild(link);
    link.style.display = 'none';
    fetch(url, { headers: { Authorization: `Token ${authToken}` } })
      .then((r) => r.blob())
      .then((blob) => {
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .finally(() => link.remove());
  }, [authToken, selectedMetricsOrdered]);

  const metricsProgressPct = useMemo(() => {
    if (!runStatus || runStatus.status !== 'running') return 0;
    const total = runStatus.run_total ?? runStatus.total_count ?? 0;
    const processed = runStatus.run_processed ?? runStatus.processed_count ?? 0;
    if (!total) return 0;
    return Math.min(100, Math.round((Number(processed) / Number(total)) * 100));
  }, [runStatus]);

  const handleUpdateSelectedMetrics = useCallback(async () => {
    if (
      selectedLocationIds.length === 0 ||
      !authToken ||
      updateMetricsLoading ||
      runStatus?.status === 'running'
    ) {
      return;
    }
    setUpdateMetricsLoading(true);
    try {
      await axios.post(
        API_ENDPOINTS.regulatorProcess,
        { location_ids: selectedLocationIds },
        { headers: { Authorization: `Token ${authToken}`, 'Content-Type': 'application/json' } }
      );
      pendingManualUpdateCountRef.current = selectedLocationIds.length;
      setUpdateCompleteOpen(false);
      setRunStatus((prev) => ({
        ...(prev && typeof prev === 'object' ? prev : {}),
        status: 'running',
        run_total: selectedLocationIds.length,
        run_processed: 0,
      }));
      startStatusPolling();
    } finally {
      setUpdateMetricsLoading(false);
    }
  }, [selectedLocationIds, authToken, updateMetricsLoading, runStatus?.status, startStatusPolling]);

  const checkCircuitStaleness = useCallback(async () => {
    if (!authToken) return;
    try {
      const { data } = await axios.get(API_ENDPOINTS.regulatorCircuitStaleness, {
        headers: { Authorization: `Token ${authToken}` },
      });
      setCircuitStaleness({
        count: data?.stale_count || 0,
        locationIds: data?.stale_location_ids || [],
      });
    } catch {
      // Non-fatal: leave any previous staleness state untouched.
    }
  }, [authToken]);

  // Re-check staleness on mount and whenever a metrics run finishes.
  useEffect(() => {
    if (runStatus?.status === 'running') return;
    checkCircuitStaleness();
  }, [authToken, runStatus?.status, checkCircuitStaleness]);

  const handleRefreshStaleCircuits = useCallback(async () => {
    const ids = circuitStaleness.locationIds;
    if (!ids.length || !authToken || runStatus?.status === 'running') return;
    try {
      await axios.post(
        API_ENDPOINTS.regulatorProcess,
        { location_ids: ids },
        { headers: { Authorization: `Token ${authToken}`, 'Content-Type': 'application/json' } }
      );
      pendingManualUpdateCountRef.current = ids.length;
      setUpdateCompleteOpen(false);
      setRunStatus((prev) => ({
        ...(prev && typeof prev === 'object' ? prev : {}),
        status: 'running',
        run_total: ids.length,
        run_processed: 0,
      }));
      startStatusPolling();
    } catch {
      // Best-effort; the next poll/status refresh will reconcile.
    }
  }, [circuitStaleness.locationIds, authToken, runStatus?.status, startStatusPolling]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedLocationIds.length === 0 || !authToken) return;
    setDeleteLocationsLoading(true);
    try {
      await Promise.all(
        selectedLocationIds.map((id) =>
          axios.delete(API_ENDPOINTS.regulatorLocationDetail(id), {
            headers: { Authorization: `Token ${authToken}` },
          })
        )
      );
      setSelectedLocationIds([]);
      fetchLocations();
    } finally {
      setDeleteLocationsLoading(false);
    }
  }, [selectedLocationIds, authToken, fetchLocations]);

  const saveSiteName = useCallback(async (locationId) => {
    if (!authToken || !locationId) return;
    setSavingSiteNameId(locationId);
    try {
      await axios.patch(
        API_ENDPOINTS.regulatorLocationDetail(locationId),
        { site_name: editingSiteNameValue.trim() },
        { headers: { Authorization: `Token ${authToken}`, 'Content-Type': 'application/json' } }
      );
      setEditingSiteNameId(null);
      setEditingSiteNameValue('');
      fetchLocations();
    } finally {
      setSavingSiteNameId(null);
    }
  }, [authToken, editingSiteNameValue, fetchLocations]);

  const toggleLocationSelection = useCallback((id) => {
    setSelectedLocationIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const selectAllOnPage = useCallback(() => {
    setSelectedLocationIds((prev) => {
      const pageIds = locations.map((loc) => loc.id);
      const next = new Set(prev);
      pageIds.forEach((id) => next.add(id));
      return [...next];
    });
  }, [locations]);

  const clearSelection = useCallback(() => setSelectedLocationIds([]), []);

  const allOnPageSelected =
    locations.length > 0 && locations.every((loc) => selectedLocationIds.includes(loc.id));
  const toggleSelectAllOnPage = useCallback(() => {
    if (allOnPageSelected) {
      setSelectedLocationIds((prev) => {
        const pageIdSet = new Set(locations.map((loc) => loc.id));
        return prev.filter((id) => !pageIdSet.has(id));
      });
    } else {
      selectAllOnPage();
    }
  }, [locations, allOnPageSelected, selectAllOnPage]);

  useEffect(() => {
    if (!metricsSelectorOpen) {
      setMetricsDropdownPositionReady(false);
      return;
    }
    if (!metricsSelectorRef.current) return;
    const el = metricsSelectorRef.current;
    const updatePosition = () => {
      const r = el.getBoundingClientRect();
      setMetricsDropdownPosition({ left: r.left, top: r.bottom + 4 });
    };
    updatePosition();
    setMetricsDropdownPositionReady(true);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [metricsSelectorOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const trigger = metricsSelectorRef.current;
      const dropdown = metricsDropdownRef.current;
      const insideTrigger = trigger && trigger.contains(e.target);
      const insideDropdown = dropdown && dropdown.contains(e.target);
      if (!insideTrigger && !insideDropdown) {
        setMetricsSelectorOpen(false);
      }
    };
    if (metricsSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [metricsSelectorOpen]);

  useEffect(() => {
    if (currentUser !== undefined && !hasProductAccess(currentUser, 'regulator')) {
      navigate('/home', { replace: true });
    }
  }, [currentUser, navigate]);

  if (currentUser !== undefined && !hasProductAccess(currentUser, 'regulator')) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <>
      <div
        className="min-w-0 flex-none min-h-0 bg-gray-50 flex flex-col h-screen overflow-hidden"
        style={{ height: '100vh', flex: 'none' }}
      >
        {runStatus?.status === 'running' && (
          <div className="flex-shrink-0 border-b border-gray-200 bg-white">
            <div className="px-4 py-2 flex items-center justify-between gap-3 text-sm text-blue-800 bg-blue-50">
              <div className="min-w-0">
                Processing…{' '}
                {runStatus.run_total != null
                  ? `${runStatus.run_processed ?? 0} / ${runStatus.run_total}`
                  : `${runStatus.processed_count ?? 0} / ${runStatus.total_count ?? 0}`}
              </div>
              <button
                type="button"
                onClick={handleCancelRun}
                disabled={cancelRunLoading}
                className="shrink-0 rounded-md border border-red-200 bg-white px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                Cancel run
              </button>
            </div>
            <div className="h-1.5 bg-gray-100 overflow-hidden" role="progressbar" aria-valuenow={metricsProgressPct} aria-valuemin={0} aria-valuemax={100} aria-label="Computing metrics">
              <div
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${metricsProgressPct}%` }}
              />
            </div>
          </div>
        )}
        <div className="min-w-0 flex-1 min-h-0 p-4 sm:p-6 max-w-full overflow-hidden flex flex-col">
          <div className="flex-shrink-0 flex items-center justify-between flex-wrap gap-4 mb-4">
            <h1 className="text-2xl font-bold text-gray-800 font-ui">PolicyPlan - Table View</h1>
          </div>

          {runStatus?.status !== 'running' && (
            <CircuitUpdateBanner
              count={circuitStaleness.count}
              noun="location"
              onRefresh={handleRefreshStaleCircuits}
            />
          )}

          <div className="min-w-0 flex-1 min-h-0 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex flex-wrap items-center gap-3">
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search address..."
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-64 focus:ring-2 focus:ring-blue-500"
              />
              {selectedLocationIds.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={handleUpdateSelectedMetrics}
                    disabled={updateMetricsLoading || runStatus?.status === 'running'}
                    className="px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {updateMetricsLoading ? 'Updating…' : `Update metrics (${selectedLocationIds.length})`}
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteSelected}
                    disabled={deleteLocationsLoading}
                    className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    <FaTrash />
                    Delete ({selectedLocationIds.length})
                  </button>
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="text-sm text-gray-600 hover:underline"
                  >
                    Clear selection
                  </button>
                </>
              )}
              <div className="relative" ref={metricsSelectorRef}>
                <button
                  type="button"
                  onClick={() => setMetricsSelectorOpen((o) => !o)}
                  className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <FaColumns />
                  Select metrics
                  <FaChevronDown className={`w-3.5 h-3.5 transition-transform ${metricsSelectorOpen ? 'rotate-180' : ''}`} />
                </button>
                {metricsSelectorOpen &&
                  metricsDropdownPositionReady &&
                  createPortal(
                    <div
                      ref={metricsDropdownRef}
                      className="fixed w-[480px] max-h-[70vh] overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg p-3"
                      style={{
                        left: metricsDropdownPosition.left,
                        top: metricsDropdownPosition.top,
                        zIndex: 9999,
                      }}
                    >
                      <p className="text-xs text-gray-500 mb-2">Choose columns to display. Hover for description.</p>
                      <label className="flex items-start gap-2 py-1.5 px-2 mb-2 rounded border border-gray-200 bg-gray-50 hover:bg-gray-100 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showAdditionalMatrices}
                          onChange={() =>
                            setShowAdditionalMatrices((prev) => {
                              if (prev) setExpandedRowId(null);
                              return !prev;
                            })
                          }
                          className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-800">
                          Additional Matrices
                          <span className="block text-xs text-gray-500">
                            Per-circuit multi-year available load capacity, A-Bank outlook, and substation outlook (SCE). Adds an expand toggle to each row.
                          </span>
                        </span>
                      </label>
                      <div className="space-y-3">
                        {Object.entries(metricsByCategory).map(([category, items], idx) => {
                          const categoryKeys = items.map((m) => m.key);
                          const allSelected = categoryKeys.length > 0 && categoryKeys.every((k) => selectedMetricKeys.includes(k));
                          const someSelected = categoryKeys.some((k) => selectedMetricKeys.includes(k));
                          return (
                            <div key={category}>
                              <div className={`text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200 pb-1.5 mb-1 ${idx === 0 ? 'pt-0' : 'pt-2'}`}>
                                {category}
                              </div>
                              <label className="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-50 cursor-pointer mb-0.5">
                                <input
                                  type="checkbox"
                                  checked={allSelected}
                                  ref={(el) => { categorySelectAllRefs.current[category] = el; }}
                                  onChange={() => toggleCategorySelection(categoryKeys)}
                                  className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-xs font-semibold text-gray-600">Select all</span>
                              </label>
                              <div className="space-y-0.5">
                                {items.map((m) => (
                                  <label
                                    key={m.key}
                                    className="flex items-start gap-2 py-1 px-2 rounded hover:bg-gray-50 cursor-pointer"
                                    title={m.description}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedMetricKeys.includes(m.key)}
                                      onChange={() => toggleMetricSelection(m.key)}
                                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-800">
                                      {m.label}
                                      {LEGACY_METRIC_KEYS.has(m.key) && ' (Legacy)'}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>,
                    document.body
                  )}
              </div>
              <button
                type="button"
                onClick={handleExportXlsx}
                className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <FaFileExcel />
                Export Excel
              </button>
            </div>

            {totalCount > PAGE_SIZE && (
              <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-4 text-sm">
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="px-2 py-1">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
                <span className="text-gray-600">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
                </span>
              </div>
            )}

            {loading ? (
              <div className="flex-1 min-h-0 p-8 text-center text-gray-500">Loading…</div>
            ) : (
              <div className="relative flex-1 min-h-0 overflow-hidden">
                <div
                  ref={tableScrollRef}
                  onScroll={handleTableScroll}
                  className={`h-full overflow-auto ${horizontalScrollVisible ? 'pb-4' : ''}`}
                >
                <table
                  ref={tableRef}
                  className="min-w-full divide-y divide-gray-200 text-sm"
                  style={{ borderCollapse: 'separate', borderSpacing: 0 }}
                >
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        className="sticky top-0 left-0 z-50 px-2 py-2 text-left bg-gray-50"
                        style={{ minWidth: `${SELECT_COLUMN_WIDTH}px`, width: `${SELECT_COLUMN_WIDTH}px` }}
                      >
                        <input
                          type="checkbox"
                          checked={allOnPageSelected}
                          onChange={toggleSelectAllOnPage}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          aria-label="Select all on page"
                        />
                      </th>
                      <th
                        className="sticky top-0 z-40 px-4 py-2 text-left font-medium text-gray-700 bg-gray-50"
                        style={{ left: `${SITE_NAME_COLUMN_LEFT}px`, minWidth: `${SITE_NAME_COLUMN_WIDTH}px`, width: `${SITE_NAME_COLUMN_WIDTH}px` }}
                      >
                        Site name
                      </th>
                      <th
                        className="sticky top-0 z-40 px-4 py-2 text-left font-medium text-gray-700 bg-gray-50"
                        style={{
                          left: `${ADDRESS_COLUMN_LEFT}px`,
                          minWidth: `${ADDRESS_COLUMN_WIDTH}px`,
                          width: `${ADDRESS_COLUMN_WIDTH}px`,
                          boxShadow: '2px 0 0 0 rgb(229 231 235)',
                        }}
                      >
                        Address
                      </th>
                      <th className="sticky top-0 z-20 px-4 py-2 text-left font-medium text-gray-700 bg-gray-50">Lat</th>
                      <th className="sticky top-0 z-20 px-4 py-2 text-left font-medium text-gray-700 bg-gray-50">Lng</th>
                      <th className="sticky top-0 z-20 px-4 py-2 text-left font-medium text-gray-700 bg-gray-50">Status</th>
                      {selectedMetricsOrdered.map((key) => (
                        <th key={key} className="sticky top-0 z-20 px-4 py-2 text-left font-medium text-gray-700 bg-gray-50">
                          {keyToLabel[key] || key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {locations.map((loc) => {
                      const m = loc.metrics;
                      const isRowExpanded = expandedRowId === loc.id;
                      return (
                        <React.Fragment key={loc.id}>
                        <tr
                          className={`group hover:bg-gray-50 ${selectedLocationIds.includes(loc.id) ? 'bg-blue-50/50' : ''}`}
                        >
                          <td
                            className={`sticky left-0 z-30 px-2 py-2 group-hover:bg-gray-50 ${selectedLocationIds.includes(loc.id) ? 'bg-blue-50/50' : 'bg-white'}`}
                            style={{ minWidth: `${SELECT_COLUMN_WIDTH}px`, width: `${SELECT_COLUMN_WIDTH}px` }}
                          >
                            <div className="flex items-center gap-1">
                              {showAdditionalMatrices && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedRowId((prev) => (prev === loc.id ? null : loc.id))}
                                  className="text-gray-400 hover:text-gray-700 p-0.5"
                                  aria-label={expandedRowId === loc.id ? 'Collapse additional matrices' : 'Expand additional matrices'}
                                  aria-expanded={expandedRowId === loc.id}
                                  title="Additional Matrices"
                                >
                                  <FaChevronRight
                                    size={10}
                                    className={`transition-transform duration-150 ${expandedRowId === loc.id ? 'rotate-90' : ''}`}
                                  />
                                </button>
                              )}
                              <input
                                type="checkbox"
                                checked={selectedLocationIds.includes(loc.id)}
                                onChange={() => toggleLocationSelection(loc.id)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                aria-label={`Select ${loc.normalized_address || loc.raw_address}`}
                              />
                            </div>
                          </td>
                          <td
                            className={`sticky z-20 px-4 py-2 text-gray-800 group-hover:bg-gray-50 ${selectedLocationIds.includes(loc.id) ? 'bg-blue-50/50' : 'bg-white'}`}
                            style={{ left: `${SITE_NAME_COLUMN_LEFT}px`, minWidth: `${SITE_NAME_COLUMN_WIDTH}px`, width: `${SITE_NAME_COLUMN_WIDTH}px` }}
                          >
                            {editingSiteNameId === loc.id ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={editingSiteNameValue}
                                  onChange={(e) => setEditingSiteNameValue(e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                  placeholder="Site name"
                                />
                                <button
                                  type="button"
                                  onClick={() => saveSiteName(loc.id)}
                                  disabled={savingSiteNameId === loc.id}
                                  className="text-xs text-blue-700 hover:underline disabled:opacity-50"
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { setEditingSiteNameId(null); setEditingSiteNameValue(''); }}
                                  className="text-xs text-gray-500 hover:underline"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingSiteNameId(loc.id);
                                  setEditingSiteNameValue(loc.site_name || '');
                                }}
                                className="text-left text-xs text-blue-600 hover:underline"
                                title={loc.site_name || 'Add site name'}
                              >
                                {loc.site_name || 'Add site name'}
                              </button>
                            )}
                          </td>
                          <td
                            className={`sticky z-10 px-4 py-2 text-gray-800 truncate group-hover:bg-gray-50 ${selectedLocationIds.includes(loc.id) ? 'bg-blue-50/50' : 'bg-white'}`}
                            style={{
                              left: `${ADDRESS_COLUMN_LEFT}px`,
                              minWidth: `${ADDRESS_COLUMN_WIDTH}px`,
                              width: `${ADDRESS_COLUMN_WIDTH}px`,
                              boxShadow: '2px 0 0 0 rgb(229 231 235)',
                            }}
                            title={loc.normalized_address || loc.raw_address}
                          >
                            {loc.normalized_address || loc.raw_address}
                          </td>
                          <td className="px-4 py-2 text-gray-600">{loc.latitude != null ? Number(loc.latitude).toFixed(4) : '—'}</td>
                          <td className="px-4 py-2 text-gray-600">{loc.longitude != null ? Number(loc.longitude).toFixed(4) : '—'}</td>
                          <td className="px-4 py-2">
                            <span className={`px-1.5 py-0.5 rounded text-xs ${loc.processing_status === 'completed' ? 'bg-green-100 text-green-800' : loc.processing_status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'}`}>
                              {loc.processing_status}
                            </span>
                          </td>
                          {selectedMetricsOrdered.map((key) => {
                            const raw = m && key in m ? m[key] : (loc[key] ?? null);
                            if (key === 'capacity_assessment_status') {
                              const meta = getAssessmentMetaByLabel(raw);
                              return (
                                <td key={key} className="px-4 py-2 text-gray-600">
                                  {meta ? (
                                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${meta.badgeClass}`}>
                                      {meta.label}
                                    </span>
                                  ) : (
                                    formatMetricValue(key, raw)
                                  )}
                                </td>
                              );
                            }
                            return (
                              <td key={key} className="px-4 py-2 text-gray-600">
                                {formatMetricValue(key, raw)}
                              </td>
                            );
                          })}
                        </tr>
                        {showAdditionalMatrices && isRowExpanded && (
                          <tr className="bg-gray-50/60">
                            <td
                              colSpan={6 + selectedMetricsOrdered.length}
                              className="px-4 py-3"
                            >
                              <div className="max-w-2xl">
                                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                  Additional Matrices
                                </div>
                                <AdditionalMatrices
                                  nearestAlc={m ? m.nearest_circuit_alc : null}
                                  maxAlc={m ? m.max_nearby_circuit_alc : null}
                                  nearestName={m ? m.nearest_circuit_name : null}
                                  maxName={m ? m.max_nearby_circuit_name : null}
                                  substationOutlook={m ? m.nearest_substation_outlook : null}
                                  substationName={m ? m.nearest_substation_name : null}
                                />
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                {locations.length === 0 && (
                  <div className="p-8 text-center text-gray-500">No locations found.</div>
                )}
                </div>

                {horizontalScrollVisible && (
                  <div className="absolute bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white/95 backdrop-blur">
                    <div
                      ref={horizontalScrollRef}
                      onScroll={handleHorizontalScroll}
                      className="overflow-x-auto overflow-y-hidden"
                      aria-label="Horizontal table scrollbar"
                    >
                      <div style={{ width: `${horizontalScrollWidth}px`, height: 14 }} />
                    </div>
                  </div>
                )}
              </div>
            )}
            {totalCount > PAGE_SIZE && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center gap-4 text-sm">
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="px-2 py-1">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
                <span className="text-gray-600">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {updateCompleteOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="table-update-complete-title"
          onClick={() => setUpdateCompleteOpen(false)}
        >
          <div className="absolute inset-0 bg-black/30" />
          <div
            className="relative w-full max-w-sm bg-white rounded-lg shadow-xl border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5">
              <h3 id="table-update-complete-title" className="text-lg font-semibold text-gray-900">
                Metrics update complete
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Metrics update finished for{' '}
                <span className="font-semibold text-gray-900">{updateCompleteCount}</span>{' '}
                location{updateCompleteCount === 1 ? '' : 's'}.
              </p>
            </div>
            <div className="px-5 py-4 bg-gray-50 rounded-b-lg flex items-center justify-end">
              <button
                type="button"
                onClick={() => setUpdateCompleteOpen(false)}
                className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RegulatorTableView;
